import type { PhotoAngle } from '@jq4race/shared';

import type { DexieBlobStore } from '../../offline/infrastructure/dexie-blob-store.js';
import type { PhotoSink } from '../domain/ports/photo-sink.js';

/**
 * LocalPhotoSink — compresses a captured photo and durably stores it in the
 * Dexie `blobs` table, keyed by the current form session's future outbox id
 * (`getOutboxId()` — design D1: the outbox item id IS the form's
 * `clientRequestId`, generated by CheckinPage before any capture happens).
 * `getOutboxId` is a getter (not a fixed value) so it is read fresh on every
 * `upload()` call and always reflects the CURRENT session, even across a
 * `computeFormFingerprint` mismatch that mints a new id mid-form (Slice 1).
 *
 * NOT wired into any page yet (Slice 4 builds the port + both adapters;
 * Slice 6 switches CheckinPage from the default RemotePhotoSink to this
 * one). Flash is unaffected — it keeps using RemotePhotoSink (design D2).
 *
 * Error propagation (critical requirement — zero silent photo loss): a
 * compression failure or a `blobStore.put()` rejection (QuotaExceededError,
 * iOS storage eviction, Safari private-mode) is NEVER caught here. It
 * propagates to `usePhotoUpload`'s catch, which marks the slot 'error' and
 * shows the operator a message — the UI never shows a slot as "done" for a
 * photo that isn't actually durably stored.
 */
export class LocalPhotoSink implements PhotoSink {
  constructor(
    private readonly blobStore: DexieBlobStore,
    private readonly compress: (blob: Blob) => Promise<Blob>,
    private readonly getOutboxId: () => string,
  ) {}

  async upload(angle: PhotoAngle, blob: Blob): Promise<{ key: string }> {
    const compressed = await this.compress(blob);
    // Deterministic per (session, angle) id — second adversarial review
    // BLOCKER #1 — a retake of the SAME angle in the SAME form session
    // reuses this exact id, so `blobStore.put()` (Dexie `put`, primary-key
    // upsert) overwrites the previous blob in place instead of leaving it
    // behind as an orphan the sync handler could still (wrongly) upload
    // alongside the new one.
    const id = `${this.getOutboxId()}:${angle}`;

    // Awaited before returning — the Dexie transaction has committed (or
    // this call has thrown) by the time the caller sees a result, per the
    // durability-before-return contract (PhotoSink port doc).
    await this.blobStore.put({
      id,
      outboxId: this.getOutboxId(),
      role: angle,
      blob: compressed,
      contentType: 'image/jpeg',
      bytes: compressed.size,
    });

    return { key: id };
  }

  /** No cached upload targets to clear — storing locally has nothing to re-issue. */
  reset(): void {
    // intentionally empty
  }
}
